<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import AppButton from '@uiKit/elements/Button.vue'

// Shared chrome for the filter and sort sheets (design: Filter / Sort):
// optional grab handle, title row with a trailing action, hairline, body,
// full-width primary action.

interface Props {
  title: string
  applyLabel: string
  /** Grab handle — only when shown as a mobile bottom sheet. */
  isSheet?: boolean
}

withDefaults(defineProps<Props>(), { isSheet: false })

const emit = defineEmits<{ (e: 'apply'): void }>()
</script>

<template>
  <Box direction="col" align="center" class="options-sheet" role="dialog" :aria-label="title">
    <Box v-if="isSheet" class="options-sheet__handle" aria-hidden="true" />

    <Box direction="col" class="options-sheet__main">
      <Box align="center" justify="between" gap="3" class="options-sheet__header">
        <Text is="h2" type="title-lg">{{ title }}</Text>
        <slot name="header-action" />
      </Box>
      <Box class="options-sheet__rule" />
      <Box direction="col" class="options-sheet__body">
        <slot />
      </Box>
    </Box>

    <AppButton :label="applyLabel" block class="options-sheet__apply" @click="emit('apply')" />
  </Box>
</template>

<style scoped>
.options-sheet {
  width: 393px;
  max-width: 100%;
  max-height: inherit;
  gap: 26px;
  padding: 14px 24px 30px;
  box-sizing: border-box;
  background: var(--p-surface-0);
}

/* Bottom sheet spans the viewport width. */
.options-sheet:has(.options-sheet__handle) {
  width: 100%;
}

.options-sheet__handle {
  width: 134px;
  height: 5px;
  border-radius: 100px;
  background: var(--p-surface-300);
  flex-shrink: 0;
}

.options-sheet__main {
  width: 100%;
  min-height: 0;
  gap: 18px;
}

.options-sheet__header {
  min-height: 28px;
}

.options-sheet__rule {
  height: 1px;
  width: 100%;
  background: var(--p-surface-100);
}

.options-sheet__body {
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
}

.options-sheet__body::-webkit-scrollbar {
  display: none;
}

.options-sheet__apply {
  min-height: 44px;
  border-radius: 12px;
}
</style>
